import { useEffect, useState } from "react";
import { Button, Modal, Form, Row, Col, Tabs, Tab } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import TableDesign from "../../common/table/TableDesign";
import { useDispatch, useSelector } from "react-redux";
import { getProfile } from "../../../store/actions/adminAction";
import { EditReportTime } from "../../../store/actions/dashboardsAction";
import ApiResponsePopup from "../../common/modal/ApiResponsePopup";
import Loader from "../../common/loader/Loader";
import { ANOTHERDATE } from "./fieldData";
import moment from "moment/moment";

const daysOfWeek = [
  { label: "Monday", value: 1 },
  { label: "Tuesday", value: 2 },
  { label: "Wednesday", value: 3 },
  { label: "Thursday", value: 4 },
  { label: "Friday", value: 5 },
  { label: "Saturday", value: 6 },
  { label: "Sunday", value: 0 },
];

const tableHeadings = [
  { label: "S. No." },
  { label: "Reports Generate Time" },
  { label: "Frequency" },
  { label: "Week Days" },
  { label: "Action", align: "center" },
];

const defaultModalItem = () => ({
  hour: "",
  minute: "",
  time: "00:00", // valid default for <input type="time">
  recur: {
    freq: null,
    interval: null,
    byDay: [],
    byMonthDay: null,
    anchorDate: moment().format("YYYY-MM-DD"),
  },
});

const ReportTimeList = () => {
  const dispatch = useDispatch();
  const { adminDetail } = useSelector((state) => state.admin);
  const { isLoader, isRecordUpdated, error: apiError } = useSelector(
    (state) => state.dashboards
  );

  const navigate = useNavigate();

  // UI states
  const [show, setShow] = useState(false);
  const [modalTimes, setModalTimes] = useState([defaultModalItem()]);
  const [isShow, setIsShow] = useState(false); // for ApiResponsePopup behavior (kept as in your original code)
  const [isUploading, setIsUploading] = useState(true);

  // tab/edit states
  const [activeTab, setActiveTab] = useState(0);
  const [editingIndex, setEditingIndex] = useState(null);

  // removal confirm
  const [showConfirm, setShowConfirm] = useState(false);
  const [removeIndex, setRemoveIndex] = useState(null);

  useEffect(() => {
    if (isUploading) {
      dispatch(getProfile()).then(({ payload }) => {
        if (payload?.data?.times) {
          // we keep modalTimes separate — don't clobber it with server list here
          setIsUploading(false);
          setIsShow(false);
        }
      });
    }
    // refetch when backend update completes
  }, [dispatch, isUploading, isRecordUpdated]);

  const navigateReport = () => navigate("/report");
  const navigateWorkforce = () => navigate("/workforce");
  const navigateReportTime = () => navigate("/report-time");

  const handleClose = () => {
    setShow(false);
    setModalTimes([defaultModalItem()]);
    setEditingIndex(null);
    setActiveTab(0);
  };

  const handleReportTime = () => {
    // open modal for ADD
    setModalTimes([defaultModalItem()]);
    setEditingIndex(null);
    setActiveTab(0);
    setShow(true);
  };

  const hideHandlerRes = () => setIsShow(false);

  // Helper: normalize a single time object (ensures hour/minute exist and are padded strings)
  const normalizeSingle = (t) => {
    // t may have hour/minute or time
    let hour = (t.hour ?? "").toString();
    let minute = (t.minute ?? "").toString();

    if (!hour || !minute) {
      const [h = "00", m = "00"] = (t.time || "00:00").split(":");
      hour = h;
      minute = m;
    }

    hour = hour.toString().padStart(2, "0");
    minute = minute.toString().padStart(2, "0");

    const freq = t.recur?.freq ?? "";
    const interval = t.recur?.interval ?? null;

    let recur = { freq, interval };

    if (freq === "DAILY") {
      recur = { freq, interval };
    } else if (freq === "WEEKLY" || freq === "BIWEEKLY") {
      recur = {
        freq: "WEEKLY",
        interval: freq === "BIWEEKLY" ? 2 : 1,
        byDay: t.recur?.byDay || [],
      };
    } else if (freq === "MONTHLY") {
      recur = {
        freq,
        interval,
        // byDay: t.recur?.byDay || [],
        byMonthDay: t.recur?.byMonthDay ?? t.recur?.byMonthDay ?? null,
        anchorDate: t.recur?.anchorDate || moment().format("YYYY-MM-DD"),
      };
    } else {
      // no freq (blank) — keep whatever recur fields exist
      recur = {
        freq: t.recur?.freq ?? null,
        interval: t.recur?.interval ?? null,
        byDay: t.recur?.byDay ?? [],
        byMonthDay: t.recur?.byMonthDay ?? null,
        anchorDate: t.recur?.anchorDate ?? moment().format("YYYY-MM-DD"),
      };
    }

    return {
      hour,
      minute,
      recur,
    };
  };

  // Helper: build payload and dispatch
  const updateTimes = (finalTimes) => {
    const payloadTimes = finalTimes.map((t) => normalizeSingle(t));
    dispatch(EditReportTime({ times: payloadTimes })).then(({ payload }) => {
      if (payload?.success) {
        // re-fetch server list
        setIsUploading(true);
        setIsShow(false);
        setShow(false);
        setEditingIndex(null);
        setModalTimes([defaultModalItem()]);
      } else {
        // show popup (kept same behavior as your original code)
        setIsShow(true);
      }
    });
  };

  // Save handler (modalTimes -> merge into adminDetail.times)
  const handleSaveTimes = (modalTimesArg) => {
    const modalList = modalTimesArg || modalTimes;
    const existing = [...(adminDetail?.times || [])];

    let finalTimes = [...existing];

    if (editingIndex !== null && editingIndex >= 0 && editingIndex < finalTimes.length) {
      // replace edited item with the first modal entry
      finalTimes[editingIndex] = modalList[0];
      // if user added extra tabs while editing, append them
      if (modalList.length > 1) {
        finalTimes = finalTimes.concat(modalList.slice(1));
      }
    } else {
      // adding new ones -> append all modal entries
      finalTimes = finalTimes.concat(modalList);
    }

    // call update helper
    updateTimes(finalTimes);
  };

  // Edit handler - load server item into modalTimes with time/hour/minute synced
  const handleEditTime = (index) => {
    const t = (adminDetail?.times && adminDetail.times[index]) || null;
    if (!t) return;

    const hour = (t.hour ?? "00").toString().padStart(2, "0");
    const minute = (t.minute ?? "00").toString().padStart(2, "0");
    const timeStr = `${hour}:${minute}`;

    setModalTimes([
      {
        ...t,
        hour,
        minute,
        time: timeStr,
        // ensure recur exists
        recur: {
          freq: t.recur?.freq ?? null,
          interval: t.recur?.interval ?? null,
          byDay: t.recur?.byDay ?? [],
          byMonthDay: t.recur?.byMonthDay ?? null,
          anchorDate: t.recur?.anchorDate ?? moment().format("YYYY-MM-DD"),
        },
      },
    ]);
    setEditingIndex(index);
    setActiveTab(0);
    setShow(true);
  };

  // Add another tab inside modal
  const addModalTime = () => {
    setModalTimes((prev) => {
      const next = [...prev, defaultModalItem()];
      setActiveTab(prev.length); // switch to newly added tab
      return next;
    });
  };

  // Remove a tab inside modal
  const removeModalTimeField = (index) => {
    setModalTimes((prev) => {
      const next = prev.filter((_, i) => i !== index);
      setActiveTab(0);
      return next.length ? next : [defaultModalItem()];
    });
  };

  // Confirm removal from server list
  const confirmRemove = () => {
    if (removeIndex === null) {
      setShowConfirm(false);
      return;
    }
    const newTimes = (adminDetail?.times || []).filter((_, i) => i !== removeIndex);
    setShowConfirm(false);
    setRemoveIndex(null);
    // call same update flow
    updateTimes(newTimes);
  };

  return (
    <>
      {isLoader && <Loader />}

      {/* Confirm remove */}
      {showConfirm && (
        <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Removal</Modal.Title>
          </Modal.Header>
          <Modal.Body>Are you sure you want to remove this report time?</Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowConfirm(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmRemove}>
              Remove
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Add / Edit Modal */}
      {show && (
        <Modal show={show} onHide={handleClose} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{editingIndex !== null ? "Edit Report Time" : "Add Report Time"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(Number(k))} className="mb-3">
              {modalTimes.map((timeObj, index) => (
                <Tab eventKey={index} title={`Time ${index + 1}`} key={index}>
                  <Form>
                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Time</Form.Label>
                          <Form.Control
                            type="time"
                            value={timeObj.time || `${timeObj.hour || "00"}:${timeObj.minute || "00"}`}
                            onChange={(e) =>
                              setModalTimes((prevTimes) => {
                                const newTimes = [...prevTimes];
                                const val = e.target.value || "00:00";
                                const [h = "00", m = "00"] = val.split(":");
                                newTimes[index] = {
                                  ...newTimes[index],
                                  time: val,
                                  hour: h,
                                  minute: m,
                                };
                                return newTimes;
                              })
                            }
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Frequency</Form.Label>
                          <Form.Select
                            value={timeObj.recur?.freq || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              setModalTimes((prevTimes) => {
                                const newTimes = [...prevTimes];
                                newTimes[index] = {
                                  ...newTimes[index],
                                  recur: {
                                    ...newTimes[index].recur,
                                    freq: value,
                                    interval: value === "BIWEEKLY" ? 2 : 1,
                                  },
                                };
                                return newTimes;
                              });
                            }}
                          >
                            <option value="">Select Frequency</option>
                            <option value="DAILY">Daily</option>
                            <option value="WEEKLY">Weekly</option>
                            <option value="BIWEEKLY">BiWeekly</option>
                            <option value="MONTHLY">Monthly</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col md={12} className="mb-5">
                        <Form.Label>Week Days</Form.Label>
                        <Form.Select
                          multiple
                          name="byDay"
                          value={timeObj.recur?.freq === "DAILY" ? [] : timeObj?.recur?.byDay || []}
                          disabled={timeObj.recur?.freq === "DAILY" || timeObj.recur?.freq === "MONTHLY"}
                          onChange={(e) => {
                            const selected = Array.from(e.target.selectedOptions, (opt) => Number(opt.value));
                            setModalTimes((prevTimes) => {
                              const newTimes = [...prevTimes];
                              newTimes[index] = {
                                ...newTimes[index],
                                recur: {
                                  ...newTimes[index].recur,
                                  byDay: selected,
                                },
                              };
                              return newTimes;
                            });
                          }}
                        >
                          {daysOfWeek.map((day) => (
                            <option key={day.value} value={day.value}>
                              {day.label}
                            </option>
                          ))}
                        </Form.Select>

                        <div className="requiredNote" style={{ marginTop: 10, position: "absolute" }}>
                          Hint: Please press ctrl key to select multiple options.
                        </div>
                      </Col>

                      <Col md={12} className="mb-3">
                        <Form.Group>
                          <Form.Label>Month Days</Form.Label>
                          <Form.Select
                            name="byMonthDay"
                            value={timeObj.recur?.freq === "DAILY" ? "" : timeObj.recur?.byMonthDay || ""}
                            disabled={timeObj.recur?.freq !== "MONTHLY"}
                            onChange={(e) =>
                              setModalTimes((prevTimes) => {
                                const newTimes = [...prevTimes];
                                newTimes[index] = {
                                  ...newTimes[index],
                                  recur: {
                                    ...newTimes[index].recur,
                                    byMonthDay:  [e.target.value],
                                  },
                                };
                                return newTimes;
                              })
                            }
                          >
                            <option style={{ display: "none" }} value="">
                              Select
                            </option>
                            {ANOTHERDATE.map((day) => (
                              <option key={day.value} value={day.value}>
                                {day.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Button variant="outline-danger" onClick={() => removeModalTimeField(index)}>
                      Remove
                    </Button>
                  </Form>
                </Tab>
              ))}
            </Tabs>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="outline-primary" onClick={addModalTime}>
              + Add Another Time
            </Button>
            <Button variant="success" onClick={() => handleSaveTimes(modalTimes)}>
              Save Timings
            </Button>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      <div className="container">
        <h1 className="main-title">Report Timing</h1>
        <div className="block-section">
          <Button variant="outline-primary" className="reportBtn me-4" onClick={navigateReport}>
            Manage Reports
          </Button>
          <Button variant="outline-primary reportBtn me-4" onClick={navigateWorkforce}>
            Workforce Capacity
          </Button>
          {/* {adminDetail?.isAdmin && (
            <Button variant="outline-primary btn-primary reportBtn" onClick={navigateReportTime}>
              Set Report Time
            </Button>
          )} */}
        </div>

        <div className="block-section">
          <p className="txtHeading">You can schedule the report time.</p>
          <Button className="reportBtn1 cursor-pointer outline-light" onClick={handleReportTime}>
            Add Send Report Timing
          </Button>
        </div>

        <TableDesign className="mt-5 pt-4" {...{ title: "List of Reports Generate Time", tableHeadings }}>
          {adminDetail?.times?.length > 0 ? (
            adminDetail.times.map((time, index) => (
              <tr key={index}>
                <td align="left">{index + 1}</td>
                <td align="left">{`${time.hour || "00"}:${time.minute || "00"}`}</td>
                <td>{time?.recur?.freq === 'WEEKLY' && time?.recur?.interval === 2 ? 'BIWEEKLY' : time?.recur?.freq || "--"}</td>
                <td>
                  {time?.recur?.byDay?.length > 0
                    ? time.recur.byDay.map((day) => daysOfWeek.find((d) => d.value === day)?.label).join(", ")
                    : "--"}
                </td>
                <td align="center">
                  <Button variant="outline-success" size="sm" onClick={() => handleEditTime(index)}>
                    Edit
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="ms-2"
                    onClick={() => {
                      setRemoveIndex(index);
                      setShowConfirm(true);
                    }}
                  >
                    Remove
                  </Button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="5" align="center">
                No Report Times Added
              </td>
            </tr>
          )}
        </TableDesign>
      </div>

      {isShow && (
        <ApiResponsePopup
          {...{
            hideHandlerRes,
            isRecordAdded: isShow,
            title: "Report Time",
            successMsg: "Report time updated successfully.",
            errorRes: apiError?.message,
          }}
        />
      )}
    </>
  );
};

export default ReportTimeList;
